import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.min.css';
import oAuthServiceConfig from 'src/app/auth/services/oAuthService/oAuthServiceConfig';
import OAuthService from 'src/app/auth/services/oAuthService';

const processData = (data) => {
  const flattenedData = [];
  const flattenRowRecursive = (row, parentPath) => {
    const total = Number(row.budgets.totalPartnerMarketingValue) + Number(row.budgets.totalNfzeMarketingValue)
    const percentage = (total == 0) ? 0 : ((row.budgets.totalForecastValue / total) * 100).toFixed()
    row['percentage'] = `${percentage}%`
    const dataPath = [...parentPath, row.subCategoryName];
    flattenedData.push({ ...row, dataPath });
    if (row.subCategories) {
      row.subCategories.forEach((underling) =>
        flattenRowRecursive(underling, dataPath)
      );
    }
  };
  data.forEach((row) => flattenRowRecursive(row, []));
  return flattenedData;
};

const BudgetSummaryList = (props) => {
  const [rowData, setRowData] = useState();
  const [columnDefs, setColumnDefs] = useState([
    { field: 'budgets.totalForecastValue', headerName: 'Retail Sales' },
    { field: 'budgets.totalPartnerMarketingValue', headerName: 'Partner Marketing', editable: true, },
    { field: 'budgets.totalNfzeMarketingValue', headerName: 'NFZE Marketing' },
    { field: 'percentage', headerName: 'Percentage %' },
  ]);
  const gridRef = useRef();
  const stateRef = useRef();
  const [stateData, setStateData] = useState({ filter: '', columnState: '', columnGroupState: '' });
  const gridStateId = useRef();
  const saveGridStateName = 'budgetSummaryList';
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 200,
    };
  }, []);
  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: 'Item Name',
      field: 'subCategoryName',
      cellRendererParams: {
        suppressCount: true,
      },
      filter: 'agSetColumnFilter',
      filterParams: {
        treeList: true,
      },
      minWidth: 280,
    };
  }, []);
  const getDataPath = useMemo(() => {
    return (data) => data.dataPath;
  }, []);
  const getRowId = useMemo(() => {
    return (params) => params.data._id;
  }, []);

  const onGridReady = useCallback((params) => {
    setRowData(processData(props.subItem.subCategories))
  }, []);

  const onCellValueChanged = (event) => {
    let tree = []
    let temp = (item) => {
      tree.push({
        level: item.level,
        childIndex: item.rowIndex - item.level
      })
      if (item.level > 0) {
        temp(item.parent)
      }
    }
    temp(event.node)
    let address = "" // subCategories[0].subCategories[4]
    let payload = { ...props.subItem }
    tree = tree.sort((a, b) => a.level - b.level)
    for (let index = 0; index < tree.length; index++) {
      const element = tree[index]
      address += `subCategories[${element.childIndex}]${(tree.length - 1) === index ? "" : "."}`
    }
    eval(`payload.${address}.budgets=event.data.budgets`)
    props.handleUpdate(payload)
  };

  useEffect(() => {
    const saveState = {
      screenName: saveGridStateName,
      screenState: stateData
    }
    stateRef.current = saveState;
  }, [stateData]);

  const onFilterChanged = (params) => {
    stateData.filter = params?.api?.getFilterModel();
    setStateData(stateData)
  }

  const onColumnStateChanged = (params) => {
    stateData.columnState = params?.columnApi?.getColumnState();
    stateData.columnGroupState = params?.columnApi?.getColumnGroupState();
  }
  const onColumnGroupChange = (params) => {
    onColumnStateChanged(params);
  }

  const onFirstDataLoad = async (params) => {
    const url = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.gridState}`;
    const savedState = await OAuthService.getList(url + '/fetchState?screenName=' + saveGridStateName);
    const data = savedState?.data;

    if(data){
      gridStateId.current = data?.id;
      data.screenState.filter && params.api.setFilterModel(data?.screenState.filter);
      data.screenState.columnState && params.columnApi.applyColumnState({ state: data?.screenState?.columnState, applyOrder: true });
      data.screenState.columnGroupState && params.columnApi.setColumnGroupState(data?.screenState?.columnGroupState);
    }
  }

  useEffect(() => {
    return () => {
      const gridState = async () => {
        if (gridStateId.current) {
          try {
            const url = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.gridStates}`;
            const saveState = await OAuthService.updateData(url, gridStateId.current, stateRef.current);
          } catch (error) {
            console.log(error);
          }
        } else {
          try {
            const url = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.gridState}`;
            const saveState = await OAuthService.addData(url + '/saveState', stateRef.current);
          } catch (error) {
            console.log(error);
          }
        }
      }
      gridState();
    }
  }, []);

  return (
    <>
      <div className="ag-theme-alpine-dark" style={{ height: 550, width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          treeData={true}
          animateRows={true}
          groupDefaultExpanded={-1}
          getDataPath={getDataPath}
          getRowId={getRowId}
          onGridReady={onGridReady}
          onCellValueChanged={onCellValueChanged}
          onFirstDataRendered={onFirstDataLoad}
          onFilterChanged={onFilterChanged}
          onSortChanged={onColumnStateChanged}
          onColumnVisible={onColumnStateChanged}
          onColumnPinned={onColumnStateChanged}
          onColumnResized={onColumnStateChanged}
          onColumnMoved={onColumnStateChanged}
          onColumnRowGroupChanged={onColumnGroupChange}
        ></AgGridReact>
      </div>
    </>
  )
};

export default BudgetSummaryList;